import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tvShows } from '@/mocks/tvShows';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import TVShowCard from '@/components/base/TVShowCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const show = useMemo(() => tvShows.find((s) => s.id === id), [id]);

  if (!show) {
    return (
      <>
        <ScrollToTop />
        <main>
          <Navbar />
          <div className="pt-20 min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="font-heading text-xl font-bold text-foreground-800 mb-2">未找到该资源</h1>
              <p className="text-foreground-500 mb-6">您要查找的电视剧不存在或已下架</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </div>
          <Footer />
        </main>
      </>
    );
  }

  const relatedShows = tvShows
    .filter((s) => s.id !== show.id && (s.category === show.category || s.tags.some((t) => show.tags.includes(t))))
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${show.title} - 被窝网电视剧全集免费高清在线观看`,
    description: show.description,
    datePublished: `${show.year}-01-01`,
    author: {
      '@type': 'Organization',
      name: '被窝网电影',
    },
    publisher: {
      '@type': 'Organization',
      name: '被窝网电影',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.beiwodianying.com/' },
      { '@type': 'ListItem', position: 2, name: show.category, item: `https://www.beiwodianying.com/category/${Object.entries({ '国产剧': 'guochanju', '韩剧': 'hanju', '美剧': 'meiju', '日剧': 'riju', '动漫': 'dongman', '综艺': 'zongyi' } as Record<string, string>).find(([k]) => k === show.category)?.[1] || 'guochanju'}` },
      { '@type': 'ListItem', position: 3, name: show.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <main>
        <Navbar />
        <div className="pt-20 pb-4 bg-background-100">
          <div className="max-w-7xl mx-auto w-full">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: show.category, href: `/category/${Object.entries({ '国产剧': 'guochanju', '韩剧': 'hanju', '美剧': 'meiju', '日剧': 'riju', '动漫': 'dongman', '综艺': 'zongyi' } as Record<string, string>).find(([k]) => k === show.category)?.[1] || 'guochanju'}` },
                { label: show.title },
              ]}
            />
          </div>
        </div>

        {/* Article Header */}
        <section className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-100">
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-full md:w-72 flex-shrink-0">
                <div className="rounded-xl overflow-hidden aspect-[2/3]">
                  <img
                    src={show.image}
                    alt={show.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  {show.title}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-md whitespace-nowrap">
                    {show.category}
                  </span>
                  <span className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-md whitespace-nowrap">
                    {show.status}
                  </span>
                  {show.isHot && (
                    <span className="px-2.5 py-1 bg-primary-500 text-white text-xs font-medium rounded-md whitespace-nowrap">
                      热播
                    </span>
                  )}
                  {show.isNew && (
                    <span className="px-2.5 py-1 bg-accent-500 text-white text-xs font-medium rounded-md whitespace-nowrap">
                      最新
                    </span>
                  )}
                  <span className="text-foreground-500 text-xs">{show.year}年</span>
                  <span className="text-foreground-500 text-xs">{show.region}</span>
                </div>

                <div className="flex items-center gap-1.5 mb-4">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="text-lg font-bold text-accent-600">{show.rating}</span>
                  <span className="text-foreground-400 text-xs">/ 10</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {show.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="space-y-2 mb-5 text-sm">
                  <p className="text-foreground-600">
                    <span className="font-medium text-foreground-800">导演：</span>
                    {show.director}
                  </p>
                  <p className="text-foreground-600">
                    <span className="font-medium text-foreground-800">主演：</span>
                    {show.actors.join(' / ')}
                  </p>
                  <p className="text-foreground-600">
                    <span className="font-medium text-foreground-800">集数：</span>
                    {show.updateInfo}
                  </p>
                </div>

                <Link
                  to={`/category/${Object.entries({ '国产剧': 'guochanju', '韩剧': 'hanju', '美剧': 'meiju', '日剧': 'riju', '动漫': 'dongman', '综艺': 'zongyi' } as Record<string, string>).find(([k]) => k === show.category)?.[1] || 'guochanju'}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line"></i>
                  开始观看
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-3xl">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4 flex items-center gap-2">
                <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                剧情简介
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                {show.longDescription}
              </p>
            </div>
          </div>
        </section>

        {/* Episode List */}
        <section className="w-full px-4 md:px-6 py-8 bg-background-100">
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-3xl">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4 flex items-center gap-2">
                <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                剧集列表
              </h2>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                {Array.from({ length: show.episodes }, (_, i) => i + 1).map((ep) => (
                  <button
                    key={ep}
                    className="px-3 py-2 bg-background-50 hover:bg-primary-50 hover:text-primary-700 text-foreground-700 text-xs font-medium rounded-lg border border-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    第{ep}集
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Shows */}
        {relatedShows.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto w-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="font-heading text-xl font-bold text-foreground-950">
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedShows.map((s) => (
                  <TVShowCard key={s.id} show={s} />
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </main>
    </>
  );
}